/**
 * Deterministic row-flow packer for batch tile creation (S21 starter
 * dashboards). Given per-type default sizes in display order, places tiles
 * left-to-right on the 12-column grid, wrapping into a new "shelf" (row band)
 * when the remaining width runs out. Before wrapping, a tile first tries to
 * slot into unused space *beneath an earlier, shorter tile in the current
 * shelf* (topmost-leftmost gap first) — so four 3×2 stats next to a 6×4 chart
 * fill the chart's row as a 2×2 block instead of leaving holes, while two
 * stats simply sit side by side. Each new shelf starts at the tallest bottom
 * of the previous one. Pure and order-preserving: `result[i]` is the layout
 * for `sizes[i]`, and identical input always yields identical output.
 */
import type { TileLayout } from '../types/dashboardTypes';
import { GRID_COLS } from './gridLayout';

export type TileSize = { w: number; h: number };

const overlaps = (a: TileLayout, b: TileLayout): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * Finds the topmost-leftmost gap beneath an already-placed tile of the
 * current shelf that fits `w`×`h` WITHOUT growing the shelf. Returns null
 * when no such gap exists (caller wraps to a new shelf).
 */
function findShelfSlot(
  shelf: TileLayout[],
  shelfBottom: number,
  w: number,
  h: number,
): { x: number; y: number } | null {
  const anchors = shelf
    .map((t) => ({ x: t.x, y: t.y + t.h }))
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (const anchor of anchors) {
    if (anchor.x + w > GRID_COLS) continue;
    if (anchor.y + h > shelfBottom) continue;
    const rect: TileLayout = { x: anchor.x, y: anchor.y, w, h };
    if (shelf.some((t) => overlaps(rect, t))) continue;
    return { x: anchor.x, y: anchor.y };
  }
  return null;
}

export function packTileLayouts(sizes: ReadonlyArray<TileSize>): TileLayout[] {
  const layouts: TileLayout[] = [];
  let shelfY = 0; // top of the current shelf
  let shelfH = 0; // height of the tallest tile in the current shelf
  let cursorX = 0; // next left-to-right position in the current shelf
  let shelf: TileLayout[] = []; // everything placed in the current shelf

  for (const size of sizes) {
    const w = Math.min(Math.max(1, size.w), GRID_COLS);
    const h = Math.max(1, size.h);
    if (cursorX + w > GRID_COLS) {
      const slot = findShelfSlot(shelf, shelfY + shelfH, w, h);
      if (slot) {
        const placed: TileLayout = { ...slot, w, h };
        shelf.push(placed);
        layouts.push(placed);
        continue;
      }
      shelfY += shelfH;
      shelfH = 0;
      cursorX = 0;
      shelf = [];
    }
    const placed: TileLayout = { x: cursorX, y: shelfY, w, h };
    shelf.push(placed);
    layouts.push(placed);
    cursorX += w;
    shelfH = Math.max(shelfH, h);
  }

  return layouts;
}
